import * as moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { firstValueFrom, forkJoin, of, Subscription } from 'rxjs';
import { GasprecioService } from '../../../services/gasprecio.service';
import { PipesExportService } from '../../../services/pipes-export.service';
import { AdjustmentLine, PipesAdjustmentsService, ProductLabeler } from '../../../services/pipes-adjustments.service';
import * as pipesRows from '../../../utils/pipes-rows';
import { buildGroupIndex, GroupIndex, groupFilterChanges$, groupFilterFromLabel, mergeGroupIndexes, ProfileCacheService, recallGroupFilter, rememberGroupFilter } from 'src/app/core/services/profile-cache.service';
import { FuelOrdersService } from '../../../services/fuel-orders.service';
import { SharedService } from '../../../services/shared.service';

/**
 * Vista ADMIN (MOBICO) de las solicitudes de pipas: una sola tabla, UNA fila por
 * pedido y SIN desglose de historial (decisión del equipo: manejarlo simple). El
 * Tipo y el estatus de cada fila reflejan la solicitud en curso: si el pedido tiene
 * un cambio en vuelo, la fila muestra ese cambio (y Aprobar/Rechazar responden ese
 * cambio); si no, muestra el pedido tal cual. La columna Tipo conserva los colores
 * de las secciones del formato oficial MOBICO. Al aprobar un cambio, el backend lo
 * aplica al pedido original. La ve el usuario admin de MOBICO al entrar a
 * "Solicitud de Pipas" (ver isMobicoPipesAdmin en fuel-orders.service).
 */
@Component({
  selector: 'app-pipes-admin',
  templateUrl: './pipes-admin.component.html',
  styleUrls: ['./pipes-admin.component.scss']
})
export class PipesAdminComponent implements OnInit, OnDestroy {

  // Modo estación (página /gasprecio/solicitud-pipas del admin): el panel queda
  // segmentado a la estación seleccionada — el filtro de estación se fija al
  // idStation del localStorage (mismo que usa la vista del solicitante) y se
  // ocultan los filtros de grupo/zona/estación (solo queda la semana).
  @Input() onlyStation = false;

  // Modo MODAL-ONLY (Etapa 3 pendientes 2026-08-03, espejo del modalOnly de
  // app-pipes): la instancia NO pinta el panel — solo consume la acción
  // aprobar/rechazar dejada por los chips ámbar de Análisis de Pedido /
  // Inventario R/T (nextPendingAction del servicio) y abre el modal REAL de
  // respuesta. El host la crea al vuelo y la destruye al recibir modalClosed
  // (true = se respondió la solicitud, refresca sus chips).
  @Input() modalOnly = false;
  @Output() modalClosed = new EventEmitter<boolean>();

  // La carga inicial se difiere a ngOnInit (los BehaviorSubject de SharedService
  // emiten dentro del constructor). Mismo patrón que PipesComponent.
  private inited = false;

  // Instancia de la ngx-datatable de Solicitudes (vive tras *ngIf="reqst === 1").
  // Se usa para recalculate(): ver scheduleTableRecalc.
  @ViewChild(DatatableComponent) private requestsTable?: DatatableComponent;

  endDate;
  idGroup = '60afa99655f4ee369d1df409';
  idGroupSub$!: Subscription;
  // '' fijo: esta vista no filtra por zona (el select se quitó del header y el
  // componente no se suscribe a sharedZone; ver comentario del constructor).
  idZone = '';
  loading = false;
  reqst = 0;
  rol;
  startDate;
  weekSelected: any;
  weekSelected$!: Subscription;
  weeksSelected$?: Subscription;
  /** Semanas elegidas en el selector múltiple (null = selector de una semana). */
  private selectedIntervals: any[] | null = null;
  private intervalsSig = '';
  private appliedIntervalsSig = '';

  // Aviso único de solicitudes NUEVAS por revisar (ids ya avisados en localStorage);
  // se notifica con un toast (no bloqueante, se cierra solo), el detalle vive en la tabla.
  private readonly noticeStorageKey = 'pipesAdminNotifiedRequests';

  // Confirmación Aprobar/Rechazar: modal op-confirm de la familia del flujo (header
  // de color según la acción, tarjeta con el detalle y error inline).
  // edit = «Realizar cambio» sobre un pedido ya aprobado (mismo modal, valores vigentes; se guarda directo, sin status).
  confirmAction: { row: any; status: 'confirmed' | 'rejected'; title: string; actionLabel: string; successTitle: string; needsDeliveryDate: boolean; edit?: boolean } | null = null;
  respondingOrder = false;
  confirmError = '';
  // Fecha de entrega confirmada: la captura el admin al aprobar un pedido primario
  // (los cambios no la piden). Se precarga con la fecha requerida por la estación.
  confirmDeliveryDate = '';
  // Entrega capturada al aprobar (opcional; el PATCH la acepta): litros ACEPTADOS por Mobico (pueden diferir de los
  // solicitados). Se precargan al abrir el modal de aprobación. El costo por litro y el total ya NO se capturan (junta
  // MOBICO + Garbri 27-oct: MOBICO solo maneja litros y producto; el costo se cruza después, ya cargada la pipa).
  confirmLiters: number | null = null;
  // AJUSTES al aprobar (usuario, 16-oct): MOBICO puede aceptar otro producto, otra fecha o otros litros que los solicitados.
  // `confirmRequested` guarda lo YA solicitado (con el cambio aplicado) para compararlo con lo capturado; el API guarda el
  // ajuste con el valor anterior (historial de ambas tablas + aviso al solicitante). Sin recuadro ni motivo en el modal.
  confirmProduct = '';
  confirmRequested: { product: string; date: string; liters: number | null } | null = null;
  // Candado de inventario del Aprobar (task 2026-07-15 "check de capacidad para
  // el admin"): espacio disponible del tanque a la fecha de entrega capturada,
  // excluyendo al pedido que se aprueba. BLOQUEA Aprobar si los litros aceptados
  // lo exceden (decisión 2026-07-15). Réplica scoped del candado del solicitante
  // (patrón de duplicación tipo type-chip/op-mini-cal).
  confirmSpace: number | null = null;
  confirmSpaceLoading = false;
  // true cuando la consulta ya corrió (con o sin dato): distingue "sin datos
  // de tanque para validar" (pill gris) de "aún no se consulta" (nada).
  confirmSpaceChecked = false;
  confirmSpaceBasis: string | null = null;
  private confirmTankCapacity: number | null = null;
  private confirmInventoryUsed: number | null = null;
  private confirmInventoryDate: string | null = null;
  private confirmCarryOver: number | null = null;
  // Modal "¿Por qué no hay espacio?" del Aprobar (réplica scoped del spaceInfo
  // del solicitante): desglose de la cuenta del candado, solo lectura.
  spaceInfo: {
    tank: number | null;
    inventory: number | null;
    basis: string | null;
    invDate: string | null;
    carry: number | null;
    available: number | null;
    requested: number | null;
  } | null = null;

  // Filtro por estación (buscable). '' = todas las estaciones del grupo/zona.
  stationFilter = '';
  stationOptions: { label: string; value: string }[] = [];
  showStations = false;
  // Estaciones del grupo de la sesión (catálogo) y las que llegaron en las filas
  // (all_groups: Garbri y demás solicitantes); el dropdown muestra la unión.
  private groupStationOptions: { label: string; value: string }[] = [];
  private rowStationOptions: { label: string; value: string }[] = [];

  // ----- Filtro por GRUPO (acuerdo junta 19-ago §5.2). El panel pide con
  // all_groups=true, así que recibe pedidos de estaciones que NO son del grupo
  // de la sesión (los solicitantes piloto viven en Garbri) y que por eso
  // tampoco aparecen en el dropdown de Estación. El grupo viene por fila desde
  // el back; el catálogo se arma con lo que realmente llegó. Vacío = todo lo
  // cargado es de un solo grupo y el filtro no se pinta. -----
  groupFilter = '';
  // Buscador de la tabla (estación o PO, revisión 06-oct): filtra local, igual
  // que el de Solicitudes del solicitante.
  ordersSearch = '';
  groupFilterOptions: { value: string; label: string }[] = [];
  // Estación con la que se llegó desde el ícono de Análisis de Pedido (una sola lectura).
  private pendingOpenStation = '';
  // Catálogo ACUMULADO de grupos (perfil del admin + todo lo que ha llegado en las filas): con una
  // semana en la que solo llegan pedidos de un grupo, el filtro ya no desaparece (Edey 19-oct).
  private groupCatalog: GroupIndex | null = null;
  // Filtros de la tabla (junta 19-oct): por tipo de solicitud, por respuesta de MOBICO y por si todavía hay algo que
  // responder. Filtran lo que la fila MUESTRA (su último estatus, ver rebuildRequests).
  typeFilter = '';
  responseFilter = '';
  actionFilter = '';
  // Producto y fecha requerida (junta MOBICO + Garbri 27-oct: con varias estaciones no se ubicaban las pipas de un día).
  productFilter = '';
  requiredDateFilter = '';
  requiredDateFilterOptions: { value: string; label: string }[] = pipesRows.requiredDateOptions([]);
  readonly productFilterOptions: { value: string; label: string }[] = [
    { value: '', label: 'Todos los productos' },
    { value: 'regular', label: 'Extra' },
    { value: 'premium', label: 'Supreme' },
    { value: 'diesel', label: 'Diésel' },
  ];
  readonly typeFilterOptions: { value: string; label: string }[] = [
    { value: '', label: 'Todos los tipos' },
    { value: 'addition', label: 'Adición' },
    { value: 'cancel', label: 'Cancelación' },
    { value: 'date', label: 'Cambio de fecha' },
    { value: 'capacity', label: 'Cambio de capacidad' },
    { value: 'product', label: 'Cambio de producto' },
  ];
  readonly responseFilterOptions: { value: string; label: string }[] = [
    { value: '', label: 'Todas las respuestas' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'review', label: 'En revisión' },
    { value: 'confirmed', label: 'Confirmada' },
    { value: 'rejected', label: 'Rechazada' },
    { value: 'cancelled', label: 'Cancelada' },
  ];
  readonly actionFilterOptions: { value: string; label: string }[] = [
    { value: '', label: 'Todas las solicitudes' },
    { value: 'pending', label: 'Por responder' },
    { value: 'answered', label: 'Respondidas' },
  ];

  // Tabla única "Solicitudes": una fila por pedido. Al expandir un pedido, sus filas
  // de historial (cada cambio) se insertan debajo ocupando las mismas columnas. El
  // Tipo y el estatus de la fila padre reflejan la solicitud en curso: el cambio en
  // vuelo si lo hay, si no el pedido.
  requests: any[] = [];
  // Ancho de la columna Estación: el nombre más largo cargado, como fitStationColumn del solicitante.
  // «Super Servicio Garbri (JUAN PABLO)» se cortaba a 160px (Edey 21-oct).
  stationColWidth = 160;
  // PO: mismo ajuste (Edey 27-oct: «GLAGDS1420» se cortaba a 100px).
  poColWidth = 100;
  private fitStationColumn(): void {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) { return; }
    const probe = document.querySelector('.orders-summary datatable-body-cell');
    // La fuente se arma por partes: con font-variant-numeric en la celda, el atajo `font` de getComputedStyle
    // regresa vacío y el canvas medía con su default de 10px (≈25 % menos; Estación seguía cortada, 27-oct).
    const cs = probe ? getComputedStyle(probe) : null;
    ctx.font = cs
      ? `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
      : '13px -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
    const widestOf = (values: any[]) =>
      Math.max(0, ...values.filter((v) => v && v !== '—').map((v) => ctx.measureText(String(v)).width));
    // Estación: texto + padding de la celda (14 + 14) + ícono de dispensario con su margen (22) + aire (6). Tope 340: con
    // 280 «Super Servicio Garbri (LA LAGUNA)» se cortaba (Edey 27-oct). Medido en el portal: con la fuente bien armada
    // el canvas coincide con el layout.
    const stations = widestOf((this.allParents || []).map((r: any) => r?.station_name));
    const stationWidth = Math.min(340, Math.max(160, Math.ceil(stations) + 28 + 22 + 6));
    const pos = widestOf((this.allParents || []).map((r: any) => r?.po_number));
    const poWidth = Math.min(160, Math.max(100, Math.ceil(pos) + 28 + 6));
    if (stationWidth === this.stationColWidth && poWidth === this.poColWidth) { return; }
    this.stationColWidth = stationWidth;
    this.poColWidth = poWidth;
    // Con columnMode force, cambiar un ancho ya pintado deja el header con el reparto viejo y el body con el nuevo
    // (desfase header/celdas, Edey 27-oct): se recalcula una vez con el layout estable (ver scheduleTableRecalc).
    this.scheduleTableRecalc();
  }
  // Pedidos con el historial CERRADO a mano (por _id de pedido). Junta 20-oct: el
  // desglose viene abierto por default para que el cambio se vea sin dar clic; el
  // usuario puede cerrarlo. Se guardan los datos crudos para reconstruir sin recargar.
  collapsedHistory = new Set<string>();
  // Historiales en cierre animado: las filas siguen en la tabla con `_closing`
  // hasta que termina la animación de salida y se retiran del array.
  closingHistory = new Set<string>();
  // Identidad de fila para el bookkeeping interno de ngx-datatable (selección,
  // índices). OJO: el trackBy real del *ngFor es [trackByProp]="'_id'" en el
  // template — rowTrackingFn ignora rowIdentity y sin trackByProp trackea por
  // índice posicional (lo que re-animaba filas al insertar/quitar historial).
  rowIdentity = (row: any) => String(row?._id ?? '');
  // Render incremental (pedido 2026-07-13 "sin paginación, tipo scroll infinito"):
  // todos los pedidos viven bajo el scroll interno de la tabla y se PINTAN por
  // lotes conforme el scroll se acerca al fondo. Los datos ya están completos en
  // memoria (getData trae todo); el corte es solo de render, para no pagar el DOM
  // de una semana muy cargada de golpe — estas tablas van con scrollbarV=false a
  // propósito (las animaciones del historial necesitan filas en flujo normal).
  // El corte es por PEDIDOS: el historial expandido no cuenta y nunca se separa
  // de su pedido.
  private readonly renderBatch = 30;
  private renderLimit = 30;
  // La tabla conserva su altura fija de 15 renglones (530px con header): con
  // menos filas se rellena con placeholders "—" (ver rebuildRequests).
  private readonly minRows = 15;
  // Pedidos completos (para avisos y conteos, independientes del corte de render).
  allParents: any[] = [];
  private orderRows: any[] = [];
  private inFlightByOrder = new Map<string, any>();
  changesRows: any[] = [];
  // Empty state de la tabla: misma línea visual que el recuadro "Solicita una pipa"
  // del flujo (ícono tenue + título + texto guía). ngx-datatable usa innerHTML.
  // Getter: el texto guía se ajusta al modo estación (segmentado a una estación).
  get emptyRequestsMessage(): string {
    const hint = this.onlyStation
      ? 'Las solicitudes nuevas de esta estación aparecerán aquí.'
      : 'Las solicitudes nuevas de las estaciones aparecerán aquí.';
    return `
    <div class="pipes-empty">
      <i class="fa fa-inbox" aria-hidden="true"></i>
      <span class="pipes-empty__title">Sin solicitudes esta semana</span>
      <span class="pipes-empty__text">${hint}</span>
    </div>`;
  }

  // Nombres comerciales MOBICO (Extra/Supreme/Diésel) → interno GASPRE (regular/premium/diesel).
  readonly productOptions: { value: string; label: string }[] = [
    { value: 'regular', label: 'Extra' },
    { value: 'premium', label: 'Supreme' },
    { value: 'diesel', label: 'Diésel' },
  ];
  // Tipos de solicitud del formato MOBICO (etiqueta de la columna Tipo).
  readonly changeTypeLabels: { [key: string]: string } = {
    addition: 'Adición',
    cancel: 'Cancelación',
    date: 'Cambio de fecha',
    capacity: 'Cambio de capacidad',
    product: 'Cambio de producto',
  };
  // La columna "Respuesta MOBICO" muestra el estatus (ya no hay texto libre).
  readonly changeStatusLabels: { [key: string]: string } = {
    pending: 'Pendiente',
    review: 'En revisión',
    confirmed: 'Confirmada',
    rejected: 'Rechazada',
    cancelled: 'Cancelada',
  };

  constructor(
    public gasprecioService: GasprecioService,
    private sharedService: SharedService,
    private fuelOrders: FuelOrdersService,
    private pipesExport: PipesExportService,
    private pipesAdjustments: PipesAdjustmentsService,
    private profileCache: ProfileCacheService,
  ) {
    this.rol = localStorage.getItem('rol');
    this.idGroup = (this.rol === 'Dios') ? localStorage.getItem('group') || '60afa99655f4ee369d1df409' : localStorage.getItem('group');

    // Zona: esta vista YA NO filtra por zona (task 2026-07-15, se quitó el
    // select del header — sin columna de zona en la tabla solo estorbaba). No
    // suscribirse a sharedZone es deliberado: una zona elegida en otra vista
    // del Tablero NO debe filtrar Solicitudes en silencio. idZone queda '' fijo.

    // Lista del selector múltiple (se publica antes que el envolvente).
    this.weeksSelected$ = this.sharedService.sharedWeeksSelected.subscribe(weeks => {
      this.selectedIntervals = weeks;
      this.intervalsSig = weeks ? weeks.map((w: any) => w.start_date).join('|') : '';
    });
    this.weekSelected$ = this.sharedService.sharedWeekSelected.subscribe(weekSelected => {
      if (weekSelected !== this.weekSelected || this.intervalsSig !== this.appliedIntervalsSig) {
        this.startDate = weekSelected['start_date'];
        this.endDate = weekSelected['end_date'];
        this.appliedIntervalsSig = this.intervalsSig;
        this.requestReload();
      }
    });
    this.period$ = this.sharedService.sharedPipesPeriod.subscribe(period => {
      if (period !== this.requestPeriod) {
        this.requestPeriod = period;
        this.requestReload();
      }
    });

    this.idGroupSub$ = this.sharedService.sharedGroup.subscribe(idGroup => {
      if (idGroup !== this.idGroup && idGroup !== '') {
        this.idGroup = idGroup;
        this.idZone = '';
        // En modo estación el filtro fijo a la estación seleccionada no se toca.
        if (!this.onlyStation) {
          this.stationFilter = '';
          if (this.inited) { this.getStationsGroup(); }
        }
        this.requestReload();
      }
    });
  }

  // ---- Recargas con filtros que cambian DURANTE una carga (Uri 16-oct: «a veces sí,
  // a veces no» aparecen las solicitudes). El selector de semana publica «Actual y
  // siguiente» cuando /weeks responde, muchas veces con la primera carga ya en vuelo;
  // antes esa recarga se descartaba (`if (!this.loading)`) y la tabla se quedaba con
  // la semana actual sola, escondiendo las solicitudes de la siguiente. Ahora se
  // encola y corre al terminar; las respuestas de una carga vieja se ignoran (seq). ----
  private reloadQueued = false;
  private loadSeq = 0;

  private requestReload(): void {
    if (!this.inited) { return; }
    if (this.loading) { this.reloadQueued = true; return; }
    this.getData();
  }

  /** Cierra una carga: si llegó otro cambio mientras tanto, vuelve a cargar. */
  private finishLoad(): void {
    this.loading = false;
    if (this.reloadQueued) {
      this.reloadQueued = false;
      this.getData(true);
    }
  }

  ngOnInit(): void {
    if (this.modalOnly) {
      // Sin tabla ni fetch del panel: solo el modal de respuesta de la acción
      // pendiente. `inited` queda en false a propósito — las suscripciones del
      // constructor (grupo/semana) no deben recargar nada en este modo.
      const action = this.fuelOrders.consumePendingAction();
      if (action?.type === 'approve' && this.isActionable(action.row)) {
        this.approveOrder(action.row);
      } else if (action?.type === 'reject' && this.isActionable(action.row)) {
        this.rejectOrder(action.row);
      } else if (action?.type === 'admin-change' && this.canRequestChange(action.row)) {
        // «Cambiar» desde el Cronograma (Edey 19-oct): el mismo modal y el mismo PATCH que el botón de la tabla.
        this.openChange(action.row);
      } else {
        this.modalClosed.emit(false);
      }
      return;
    }
    this.inited = true;
    // Grupo elegido en el Cronograma de abajo (otro componente vivo en la misma vista).
    this.groupFilterSub$ = groupFilterChanges$.subscribe((label) => {
      const next = groupFilterFromLabel(this.groupFilterOptions, label, this.groupFilter);
      if (next === this.groupFilter) { return; }
      this.groupFilter = next;
      this.applyGroupFilter();
    });
    // Grupos del perfil del admin como base del catálogo (las filas de cada carga se le suman).
    this.profileCache.getStationGroupIndex(this.rol === 'Dios', this.idGroup).subscribe((index) => {
      this.groupCatalog = mergeGroupIndexes(this.groupCatalog, index);
      this.groupFilterOptions = this.groupCatalog.options;
      this.groupFilter = recallGroupFilter(this.groupCatalog.options, this.groupFilter);
    });
    if (this.onlyStation) {
      // Segmentación por estación: mismo origen que la vista del solicitante.
      // Sin estación en storage se degrada a "todas" (comportamiento previo).
      this.stationFilter = localStorage.getItem('idStation') || '';
      this.idZone = '';
      this.getData();
      return;
    }
    // Llegada desde el ícono de una estación (openPipesStation): esa estación queda
    // preseleccionada UNA vez; entrando por el menú se ven todas (jefe 16-oct: al admin
    // «no le salía ninguna» porque el panel se fijaba a la estación del header).
    // Sin dropdown de estación (Edey 19-oct), la llegada se expresa en el BUSCADOR: el nombre se
    // resuelve con las filas de la primera carga (ver rebuildRequests).
    this.pendingOpenStation = this.gasprecioService.consumePipesOpenStation() || '';
    this.getStationsGroup();
    this.getData();
  }

  ngOnDestroy(): void {
    this.idGroupSub$.unsubscribe();
    this.weekSelected$.unsubscribe();
    this.weeksSelected$?.unsubscribe();
    this.period$?.unsubscribe();
    this.groupFilterSub$?.unsubscribe();
  }

  // ---- Periodo de la tabla (revisión 08-oct: "mes actual y anterior"), compartido
  // con el solicitante y el Cronograma vía SharedService. ----
  requestPeriod = 'week';
  private period$!: Subscription;
  private groupFilterSub$?: Subscription;

  private requestRange(): { start: string; end: string } {
    if (this.requestPeriod === 'month' || this.requestPeriod === 'prev_month') {
      const m = moment().startOf('month');
      if (this.requestPeriod === 'prev_month') { m.subtract(1, 'month'); }
      return { start: m.toISOString(), end: m.clone().endOf('month').toISOString() };
    }
    return { start: this.startDate, end: this.endDate };
  }

  /** Lunes (ISO) de cada semana del rango: /fuel-orders/changes va por semana. */
  /** Lunes de cada semana del rango elegido («Actual y siguiente» abarca dos,
   *  14-oct). En UTC: start_date llega como medianoche UTC del lunes. */
  private selectedWeeks(): string[] {
    // Selector múltiple: exactamente las semanas elegidas ([] = ninguna).
    if (this.selectedIntervals) {
      return this.selectedIntervals.map((w: any) => moment.utc(w.start_date).toISOString());
    }
    const weeks: string[] = [];
    const last = moment.utc(this.endDate);
    for (const w = moment.utc(this.startDate); w.isSameOrBefore(last); w.add(7, 'days')) {
      weeks.push(w.toISOString());
    }
    return weeks.length ? weeks : [this.startDate];
  }

  /** Las solicitudes se cargan por el rango envolvente: con semanas salteadas
   *  (o ninguna) sobran filas, que se quitan aquí por su fecha requerida. */
  private rowsInSelectedWeeks(rows: any[]): any[] {
    if (!this.selectedIntervals || this.requestPeriod !== 'week') { return rows; }
    const spans = this.selectedIntervals.map((w: any) => [moment.utc(w.start_date).format('YYYY-MM-DD'), moment.utc(w.end_date).format('YYYY-MM-DD')]);
    return (rows || []).filter((r: any) => {
      const v = r.order_delivery_date || r.expected_delivery_date || r.date;
      if (!v) { return false; }
      const d = moment.utc(v).format('YYYY-MM-DD');
      return spans.some(([a, b]) => d >= a && d <= b);
    });
  }

  private weeksIn(start: string, end: string): string[] {
    const weeks: string[] = [];
    const last = moment(end);
    for (const w = moment(start).startOf('isoWeek'); w.isSameOrBefore(last); w.add(7, 'days')) {
      weeks.push(w.toISOString());
    }
    return weeks;
  }

  // La altura de la tabla vive en CSS (.orders-summary-scroll): min-height de
  // 3 filas, crece con el contenido y max-height de 15 filas con scroll interno.
  // Sin cálculos en px que se desfasen del render real.

  /** Nombre comercial MOBICO del producto interno (fallback cuando no hay material). */
  productLabel(product: string): string {
    const opt = this.productOptions.find((p) => p.value === product);
    return opt ? opt.label : (product || '—');
  }
  /** La misma etiqueta como función suelta, para PipesAdjustmentsService. */
  private readonly productLabeler: ProductLabeler = (product) => this.productLabel(product);

  /** Texto de la marca «llegó con poco aviso» de una solicitud de cambio (regla MOBICO 48/24 hrs). */
  deadlineTitle(row: any): string {
    return row?.deadline_state === 'late'
      ? 'Recibida con menos de 24 hrs de anticipación: puede que no se pueda atender.'
      : 'Recibida con menos de 48 hrs de anticipación: queda en espera de disponibilidad.';
  }

  /** Solo lo aún no respondido por MOBICO admite acciones (pedidos y cambios). */
  isActionable(row: any): boolean {
    return ['pending', 'sent', 'review'].includes(row.status);
  }

  /** Pedido ya aprobado que aún admite un cambio: confirmado, sin otra solicitud en curso y sin haber llegado. */
  canRequestChange(row: any): boolean {
    return pipesRows.canRequestChange(row);
  }

  /** Tooltip de la celda PO: en el historial, a qué PO pasó el pedido; en la fila del pedido, el PO anterior si lo cambió. */
  poColumnTitle(row: any): string {
    if (row?.is_history) { return this.historyPoTitle(row); }
    return row?.previous_po_number && row.previous_po_number !== row.po_number ? `PO anterior: ${row.previous_po_number}` : '';
  }

  /**
   * «Cambiar» del admin (usuario, 16-oct; etiqueta corta 19-oct): una vez aprobado el pedido, Aprobar/Rechazar se
   * bloquean; por si hace falta mover algo después, abre el MISMO modal de aprobar con los valores VIGENTES (lo que
   * MOBICO aceptó: producto, fecha de entrega, litros y costos). Al guardar se aplica directo (PATCH con `as_change`):
   * queda el historial con el valor anterior, el PO sigue al producto y la estación recibe el aviso, igual que un
   * ajuste al aprobar. Es el mismo botón en la tabla Solicitudes y en el Cronograma (ahí vía modalOnly).
   */
  openChange(row: any): void {
    if (!this.canRequestChange(row)) { return; }
    this.confirmError = '';
    const day = row.delivery_date || row.order_delivery_date
      ? moment.utc(row.delivery_date || row.order_delivery_date).format('YYYY-MM-DD')
      : '';
    this.confirmDeliveryDate = day;
    this.confirmLiters = row.liters ?? row.liters_requested ?? null;
    this.confirmRequested = { product: row.product || '', date: day, liters: this.confirmLiters };
    this.confirmProduct = row.product || '';
    this.confirmAction = { row, status: 'confirmed', title: 'Realizar cambio', actionLabel: 'Guardar cambio', successTitle: 'Cambio guardado', needsDeliveryDate: true, edit: true };
    this.refreshConfirmSpace();
  }

  /** Motivo del disabled de Aprobar/Rechazar (botones SIEMPRE visibles con
   *  enable/disable, task 2026-07-14 — mismo patrón que Cambiar/Cancelar del
   *  solicitante): tooltip que explica por qué ya no hay acción. */
  actionsDisabledReason(row: any): string {
    switch (row.status) {
      case 'confirmed':
        return 'La solicitud ya fue aprobada';
      case 'rejected':
        return 'La solicitud ya fue rechazada';
      case 'cancelled':
        return 'El pedido está cancelado';
      default:
        return 'La solicitud ya fue respondida';
    }
  }

  /** Cambios aún sin respuesta de MOBICO (definen qué muestra la fila del pedido). */
  private isChangeInFlight(c: any): boolean {
    return ['pending', 'sent', 'review'].includes(c.status);
  }

  /**
   * Estatus visible de una solicitud de cambio con el tipo integrado, para que el
   * historial se lea directo (ej. "Cambio aprobado", "Cancelación rechazada").
   */
  private changeStatusDisplay(kind: string, c: any): string {
    // REGLA DESACTIVADA (2026-07-06) — estatus "En espera de disponibilidad" del
    // review por la regla de 48 hrs (nunca aplicaba a cancelaciones):
    // if (kind !== 'cancel' && c.status === 'review' && (c.week_status || '').includes('espera de disponibilidad')) {
    //   return 'En espera de disponibilidad';
    // }
    const subject = kind === 'cancel' ? 'Cancelación' : (kind === 'addition' ? 'Adición' : 'Cambio');
    const fem = kind === 'cancel' || kind === 'addition';
    const byStatus: { [key: string]: string } = {
      pending: `${subject} pendiente`,
      sent: `${subject} pendiente`,
      review: `${subject} en revisión`,
      confirmed: fem ? `${subject} aprobada` : `${subject} aprobado`,
      rejected: fem ? `${subject} rechazada` : `${subject} rechazado`,
    };
    return byStatus[c.status] || this.changeStatusLabels[c.status] || c.status;
  }

  /**
   * Línea del PEDIDO dentro del historial («Adición» con su estatus), para cuando la fila de arriba muestra un cambio:
   * así el desglose lleva la solicitud completa en orden. Trae lo que se pidió originalmente (antes del evento más
   * antiguo) y va al final (es lo primero que pasó).
   */
  private orderHistoryLine(order: any, events: any[]): any {
    return {
      is_history: true,
      station_name: order.station_name,
      _id: `order-${order._id}`,
      order_id: String(order._id),
      change_kind: 'addition',
      type_label: this.changeTypeLabels['addition'],
      request_date: order.date,
      ...pipesRows.originalRequestValues(order, events),
      new_value: '—',
      po_new: null,
      history_reason: '',
      status: order.status,
      event_status: order.status,
      statusName: order.statusName,
      _ts: new Date(order.date).getTime() || 0,
    };
  }

  /**
   * Historial de cambios de un pedido como filas hijas: los cambios ya RESUELTOS por
   * MOBICO (aprobados/rechazados) y los ajustes, más reciente arriba. El cambio en
   * vuelo no se lista: ya lo muestra la fila padre (listarlo también lo duplicaba);
   * el rechazo que la fila muestra y la línea del pedido los resuelve rebuildRequests
   * (ver orderHistoryLine). Hereda el contexto del pedido (PO/Estación/Capacidad/
   * Producto/Fecha requerida) para renderizarse con todas las columnas de la tabla.
   */
  buildHistoryFor(order: any): any[] {
    // De la fila del pedido solo hereda la estación: PO, producto, fecha y capacidad de cada fila del historial son los
    // de la solicitud ANTES de ese cambio (los trae cada evento); «Nuevo valor» lleva lo que se pidió/aceptó y la fila
    // del pedido (arriba) tiene lo vigente.
    const base = {
      is_history: true,
      station_name: order.station_name,
    };
    const changes = (this.changesRows || [])
      .filter((c: any) => c.order_id === String(order._id) && !this.isChangeInFlight(c))
      .map((c: any) => ({ ...c, ...base }));
    // Ajustes de MOBICO al aprobar (otro producto / fecha / litros que los solicitados): una fila por campo, ver
    // PipesAdjustmentsService.historyRows (compartido con la tabla del solicitante).
    const adjustments = this.pipesAdjustments.historyRows(order, base, this.changeTypeLabels, this.productLabeler);
    // Lo que un ajuste no tocó se completa con el estado del pedido en ese momento (no con lo vigente hoy).
    return this.pipesAdjustments.fillAdjustmentContext([...changes, ...adjustments].sort((a: any, b: any) => b._ts - a._ts));
  }

  /**
   * Reconstruye la tabla: una fila por pedido (el cambio en vuelo si lo hay, si no el
   * pedido), ordenada por fecha de solicitud descendente; si el pedido está expandido,
   * sus filas de historial se insertan justo debajo ocupando las mismas columnas.
   */
  /** Buscador: repinta la tabla desde el primer lote. */
  onSearchChange(): void {
    this.renderLimit = this.renderBatch;
    this.rebuildRequests();
  }

  /** Cambio de los filtros de tipo / respuesta / acciones: repinta desde el primer lote. */
  onFilterChange(): void {
    this.renderLimit = this.renderBatch;
    this.rebuildRequests();
  }

  /** Tipo, producto, fecha requerida, respuesta de MOBICO y acciones sobre lo que la fila muestra. */
  private matchesRowFilters(row: any): boolean {
    if (this.typeFilter && row.change_kind !== this.typeFilter) { return false; }
    if (this.productFilter && row.product !== this.productFilter) { return false; }
    if (this.requiredDateFilter && pipesRows.requiredDay(row) !== this.requiredDateFilter) { return false; }
    const shown = row.event_status || row.status;
    const status = shown === 'sent' ? 'pending' : shown;
    if (this.responseFilter && status !== this.responseFilter) { return false; }
    if (this.actionFilter === 'pending' && !this.isActionable(row)) { return false; }
    if (this.actionFilter === 'answered' && this.isActionable(row)) { return false; }
    return true;
  }

  /** Pedidos padre que pasan todos los filtros (todos, no solo el lote pintado). */
  private filteredParents: any[] = [];

  /** Excel de las solicitudes tal como se ven (grupo, estación, búsqueda y día
   *  aplicados; sin historial): EXACTAMENTE las filas de la tabla. */
  /** Excel con el formato que MOBICO arma a mano para logística («MIERCOLES 14
   *  DE OCTUBRE.xlsx»): SHIP TO · FECHA · TERMINAL · NOMBRE · NUEVO PO · PRODUCTO ·
   *  LITROS · TRANSPORTE, solo las solicitudes APROBADAS del día elegido. SHIP TO,
   *  TERMINAL y TRANSPORTE los llena MOBICO (son catálogos suyos, no del portal). */
  async exportRequestsExcel(): Promise<void> {
    const range = this.requestRange();
    const day = await this.pipesExport.pickDay('Descargar solicitudes aprobadas', range);
    if (day === null) { return; }
    const rows = (this.filteredParents || []).filter((p: any) =>
      p.status === 'confirmed' && this.pipesExport.sameDay(p.order_delivery_date || p.date, day));
    if (!rows.length) { this.pipesExport.showEmpty('Sin solicitudes aprobadas', 'No hay solicitudes aprobadas para ese día con los filtros actuales.'); return; }
    const x = this.pipesExport;
    const dateOf = (r: any) => {
      const v = r.delivery_date || r.order_delivery_date;
      return v ? moment.utc(v).startOf('day').toDate() : null;
    };
    await x.downloadPlain({
      sheet: 'Hoja1',
      title: '',
      fileName: `Solicitudes_MOBICO_${x.daySuffix(day, range)}.xlsx`,
      columns: [
        { header: 'SHIP TO', width: 12 }, { header: 'FECHA', width: 14, numFmt: 'dd/mm/yyyy' }, { header: 'TERMINAL', width: 12 },
        { header: 'NOMBRE', width: 32 }, { header: 'NUEVO PO', width: 16 }, { header: 'PRODUCTO', width: 12 },
        { header: 'LITROS', width: 10, numFmt: '0' }, { header: 'TRANSPORTE', width: 14 },
      ],
      rows: rows.map((r: any) => [
        r.ship_to || '', dateOf(r), r.terminal || '',
        String(r.station_name || '').toUpperCase(), r.po_number || '', String(this.productLabel(r.product) || '').toUpperCase(),
        r.truck_capacity != null ? +r.truck_capacity : null, '',
      ]),
    });
  }

  /** Estación o PO, sin distinguir mayúsculas ni acentos. */
  private matchesSearch(row: any): boolean {
    const normalize = (v: any) =>
      String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const search = normalize(this.ordersSearch).trim();
    if (!search) { return true; }
    return [row.station_name, row.po_number].some((v) => normalize(v).includes(search));
  }

  /** Cambio del filtro de grupo: repinta la tabla desde el primer lote. */
  onGroupFilterChange(): void {
    rememberGroupFilter(this.groupFilterOptions, this.groupFilter);
    this.applyGroupFilter();
  }

  private applyGroupFilter(): void {
    this.renderLimit = this.renderBatch;
    this.rebuildRequests();
    // allParents ya trae las filas del grupo elegido: pide el espacio de las que faltaban.
    this.loadCapacityRisks();
  }

  private rebuildRequests(): void {
    // Última actividad por pedido: el timestamp más reciente entre sus cambios
    // (updated_at = cuándo respondió MOBICO; created_at si sigue en vuelo). Clave
    // del ORDEN de la tabla: lo recién movido —solicitado, aprobado o rechazado—
    // sube, en vez de que la fila regrese a su posición de creación al resolverse.
    const activityByOrder = new Map<string, number>();
    (this.changesRows || []).forEach((c: any) => {
      if (!c.order_id) { return; }
      const ts = new Date(c.updated_at || c.request_date).getTime() || 0;
      activityByOrder.set(c.order_id, Math.max(activityByOrder.get(c.order_id) || 0, ts));
    });

    let parents: any[] = [];
    (this.orderRows || []).forEach((r: any) => {
      // Pedidos que entran por el Excel de MOBICO (source "excel"): no son solicitudes de
      // estación; solo viven en el Cronograma y alimentan el teórico.
      if (r.source === 'excel') { return; }
      const allHistory = this.buildHistoryFor(r);
      const inflight = this.inFlightByOrder.get(String(r._id));
      // La fila muestra el ÚLTIMO estatus de la solicitud (junta 19-oct): el cambio en vuelo; si no, el último cambio
      // RECHAZADO después de la respuesta de MOBICO al pedido (utils/pipes-rows.ts; antes volvía a «Confirmada» con el
      // rechazo escondido en el historial); si no, el pedido. Cuando muestra un cambio, el historial no lo repite y
      // agrega la línea del pedido (Adición + su estatus). event_status = lo que la fila muestra (pill y filtros);
      // status sigue siendo el del pedido (Aprobar/Rechazar/Realizar cambio), salvo en el cambio en vuelo, que ES la fila.
      const lastRejected = inflight ? null : pipesRows.latestRejectedChange(allHistory, pipesRows.orderResponseTs(r));
      const history =
        inflight || lastRejected
          ? [...allHistory.filter((e: any) => e !== lastRejected), this.orderHistoryLine(r, inflight ? [...allHistory, inflight] : allHistory)].sort((a: any, b: any) => b._ts - a._ts)
          : allHistory;
      const hasHistory = history.length > 0;
      const parentRow = inflight
        // El cambio en vuelo viene del doc de cambio, que no trae el grupo de la estación: se hereda del pedido
        // (Edey 19-oct: una solicitud pendiente de Garbri no salía con el filtro Garbri; confirmada sí).
        ? { ...inflight, group: r.group, group_name: r.group_name, cluster: r.cluster, cluster_name: r.cluster_name, product: inflight.product || r.product, adjustments: r.adjustments || [], event_status: inflight.status, _order_id: String(r._id), has_history: hasHistory, _history: history }
        : lastRejected
          ? {
              ...r,
              request_date: lastRejected.request_date,
              change_kind: lastRejected.change_kind,
              type_label: lastRejected.type_label,
              new_value: lastRejected.new_value,
              new_value_title: lastRejected.history_reason || '',
              statusName: lastRejected.statusName,
              event_status: 'rejected',
              _order_id: String(r._id),
              has_history: hasHistory,
              _history: history,
            }
          : { ...r, request_date: r.date, change_kind: 'addition', type_label: this.changeTypeLabels['addition'], new_value: '—', event_status: r.status, _order_id: String(r._id), has_history: hasHistory, _history: history };
      // Actividad del pedido: lo más reciente entre el propio pedido (updated_at
      // cubre creación, edición y respuesta de MOBICO) y sus cambios.
      parentRow._activity_ts = Math.max(
        new Date(r.updated_at || r.date).getTime() || 0,
        activityByOrder.get(String(r._id)) || 0
      );
      parents.push(parentRow);
    });
    // Por FECHA DE SOLICITUD, la más reciente arriba (revisión 06-oct: "no vienen
    // del 5, vienen 4 y 6"). Antes era por última actividad y una respuesta de
    // MOBICO subía un pedido viejo entre los nuevos. Empate (mismo instante):
    // lo movido más recientemente primero. Igual en la tabla del admin y la del
    // solicitante.
    const requestTs = (row: any) => new Date(row.request_date).getTime() || 0;
    parents.sort((a: any, b: any) => requestTs(b) - requestTs(a) || b._activity_ts - a._activity_ts);

    // Catálogo de grupos con lo que realmente llegó en las filas (el back solo
    // resuelve `group` cuando la consulta fue all_groups).
    const groupIndex = mergeGroupIndexes(this.groupCatalog, buildGroupIndex(parents, (p) => String(p._id)));
    this.groupCatalog = groupIndex;
    this.groupFilterOptions = groupIndex.options;
    // Filtro «Grupo» compartido con Análisis de Pedido / Inventario R/T (Edey 16-oct);
    // si el grupo ya no existe en la carga actual se limpia para no dejar la tabla vacía.
    this.groupFilter = recallGroupFilter(groupIndex.options, this.groupFilter);
    // Estaciones para el filtro: las que de verdad llegaron (con all_groups vienen de
    // varios grupos y el catálogo del grupo de la sesión no las tiene). Solo se rearma
    // con la carga completa: filtrando por estación llegaría una sola.
    if (!this.stationFilter) {
      const seen = new Map<string, string>();
      parents.forEach((p: any) => { if (p.station_id && p.station_name) { seen.set(String(p.station_id), p.station_name); } });
      // Llegada desde el ícono de una estación: su nombre va al buscador (si no hay filas suyas en la carga, se ven todas).
      if (this.pendingOpenStation) {
        this.ordersSearch = seen.get(this.pendingOpenStation) || '';
        this.pendingOpenStation = '';
      }
      this.rowStationOptions = [...seen.entries()].map(([value, label]) => ({ label, value }));
      this.mergeStationOptions();
    }
    if (this.groupFilter) {
      parents = parents.filter((p: any) => groupIndex.byStation.get(String(p._id)) === this.groupFilter);
    }

    // Lista COMPLETA de pedidos: la usa el aviso de solicitudes nuevas, que no
    // debe depender de lo ya pintado.
    this.allParents = parents;
    this.fitStationColumn();
    this.requiredDateFilterOptions = pipesRows.requiredDateOptions(parents);

    // El buscador va DESPUÉS de allParents: escribir en él no debe esconder el
    // aviso de solicitudes nuevas.
    if (this.ordersSearch) {
      parents = parents.filter((p: any) => this.matchesSearch(p));
    }
    if (this.typeFilter || this.productFilter || this.requiredDateFilter || this.responseFilter || this.actionFilter) {
      parents = parents.filter((p: any) => this.matchesRowFilters(p));
    }
    this.filteredParents = parents;

    // Corte de render sobre los PEDIDOS ya ordenados; el historial expandido se
    // inserta después, dentro del mismo corte.
    const visibleParents = parents.slice(0, this.renderLimit);

    const result: any[] = [];
    visibleParents.forEach((p: any) => {
      // Aviso de capacidad: se calcula aquí (no en el template) para no recalcularlo en cada ciclo de detección.
      p._capRisk = this.capacityRisk(p);
      result.push(p);
      if (p.has_history && !this.collapsedHistory.has(p._order_id)) {
        p._history.forEach((ev: any) => result.push(ev));
      }
    });
    // Relleno con filas vacías hasta minRows (espejo de la vista del solicitante):
    // la tabla conserva su altura fija aunque haya pocos pedidos, sin hueco bajo
    // el último. Solo si hay ≥1 fila real (con 0 se deja vacío para que el
    // emptyMessage se muestre). _placeholder marca la fila para ocultar su
    // contenido vía CSS (ver getRowClass + .pipes-empty-row); el _id único
    // mantiene trackByProp/rowIdentity estables.
    if (result.length > 0) {
      for (let i = result.length; i < this.minRows; i++) {
        result.push({ _id: `__empty_${i}`, _placeholder: true });
      }
    }
    this.requests = result;
  }

  /**
   * UN solo recálculo de anchos tras la carga, endurecido (fix 2026-07-10 v2,
   * "columnas corridas / header y body desincronizados"): la 1.ª iteración
   * llamaba recalculate() en cada rebuild vía setTimeout, y se comprobó que el
   * recálculo mismo era quien CORROMPÍA el reparto — corría en frames donde el
   * contenedor aún no tenía su ancho final (la tabla vive tras *ngIf) y con
   * innerWidth 0 el modo force aplasta las columnas flexibles a 0px, estado
   * PEGAJOSO (el reparto es proporcional al ancho actual: una columna en 0 ya
   * no recibe nada, ni con resize). Defensas actuales: (1) pisos [minWidth] en
   * TODAS las columnas del template — el clamp impide el colapso a 0 aunque un
   * cálculo corra con ancho malo; (2) este recálculo se dispara SOLO tras
   * terminar la carga, con doble requestAnimationFrame (layout ya estabilizado,
   * no un timer ciego) y guard de ancho > 0.
   */
  private scheduleTableRecalc(): void {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = this.requestsTable?.element as HTMLElement | undefined;
      if (el && el.clientWidth > 0) {
        this.requestsTable?.recalculate();
        // recalculate() corrige los anchos MUTANDO las mismas columnas: el header (OnPush) no recibe un input nuevo y
        // se quedaba con el reparto viejo (medido 27-oct: header 1,498px contra body 1,461px, desfase acumulado desde
        // Producto). Un arreglo nuevo de columnas obliga al header a repintar sus celdas con los anchos ya corregidos.
        const table = this.requestsTable as any;
        if (table?._internalColumns) {
          table._internalColumns = [...table._internalColumns];
          table.cd?.markForCheck();
        }
        // Refresco de FILAS tras corregir el modelo de columnas (fix v3): con
        // fixed-row + virtualización, ngx-datatable reutiliza los nodos de fila
        // y algunas celdas del body conservaban el style.width fraccional del
        // cálculo anterior (p. ej. Producto 105.4px vs los 90px ya corregidos
        // del header) hasta que un hover forzaba su re-render — ese salto era
        // el corrimiento restante. Se clonan array Y objetos de fila: con
        // [trackByProp]="'_id'" + filas OnPush, la sola referencia nueva del
        // array no marca dirty a las filas reutilizadas (mismo objeto = mismo
        // input); el clon superficial sí, y el trackBy por _id conserva el DOM
        // (sin re-animaciones del historial). Repinta todas las celdas con los
        // anchos ya corregidos.
        this.requests = this.requests.map((r: any) => ({ ...r }));
      }
    }));
  }

  /** Scroll del contenedor de la tabla: al acercarse al fondo (~4 filas) se
   *  pinta el siguiente lote de pedidos. Scroll infinito client-side: los datos
   *  ya están cargados, solo se difiere el render. */
  onOrdersScroll(event: Event): void {
    if (this.renderLimit >= this.allParents.length) { return; }
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 128) {
      this.renderLimit += this.renderBatch;
      this.rebuildRequests();
    }
  }

  /**
   * Alterna el desglose de historial de un pedido (inserta/quita sus filas hijas).
   * El cierre es en DOS tiempos para que haya animación de salida (ngx-datatable
   * no tiene exit hook): primero las filas hijas se marcan `_closing` (corre la
   * animación oh-conceal y el chevron regresa), y al terminar se retiran del array.
   */
  toggleHistory(row: any): void {
    const id = row._order_id;
    if (!this.collapsedHistory.has(id)) {
      if (this.closingHistory.has(id)) { return; }
      this.closingHistory.add(id);
      this.requests = this.requests.map((r: any) =>
        (r.is_history && r.order_id === id) ? { ...r, _closing: true } : r);
      setTimeout(() => {
        this.closingHistory.delete(id);
        this.collapsedHistory.add(id);
        this.rebuildRequests();
      }, 160);
      return;
    }
    this.collapsedHistory.delete(id);
    this.rebuildRequests();
  }

  /** true si el desglose de historial del pedido está abierto (y no cerrándose). */
  isHistoryOpen(row: any): boolean {
    return !this.collapsedHistory.has(row._order_id) && !this.closingHistory.has(row._order_id);
  }

  /** Marca las filas hijas del historial, las que están saliendo y las de relleno
   *  (placeholder "—") para el CSS. */
  getRowClass = (row: any) => ({
    'history-subrow': !!row?.is_history,
    'history-closing': !!row?._closing,
    'pipes-empty-row': !!row?._placeholder,
  });

  /** Estaciones para el filtro buscable: si hay zona activa, solo las de esa zona. */
  getStationsGroup() {
    this.showStations = false;
    this.gasprecioService.getStationsGroupZone(this.idGroup, this.idZone || '').subscribe((data: any) => {
      const stations: any = data || [];
      this.groupStationOptions = stations.map((s: any) => ({ label: s.alias, value: s._id }));
      this.mergeStationOptions();
      this.showStations = true;
    });
  }

  /** Dropdown de estación = catálogo del grupo ∪ estaciones de las filas, sin repetir, por nombre. */
  private mergeStationOptions(): void {
    const byId = new Map<string, string>();
    [...this.groupStationOptions, ...this.rowStationOptions].forEach((o) => {
      if (!byId.has(String(o.value))) { byId.set(String(o.value), o.label); }
    });
    this.stationOptions = [
      { label: 'Todas las estaciones', value: '' },
      ...[...byId.entries()].map(([value, label]) => ({ label, value })).sort((a, b) => a.label.localeCompare(b.label, 'es')),
    ];
  }

  /** @param silent true = recarga interna sin loader (refresh automático). */
  getData(silent = false) {
    if (!silent) { this.reqst = 0; }
    // Toda recarga arranca con los historiales ABIERTOS (junta 20-oct: «que se vea
    // el cambio sin darle clic; si lo quieres ocultar, ok»); cerrar uno es un gesto
    // puntual que no persiste entre recargas.
    this.collapsedHistory.clear();
    this.closingHistory.clear();
    // Toda recarga (entrar, filtros, refresh) regresa al inicio del listado.
    this.renderLimit = this.renderBatch;
    this.loading = true;
    const seq = ++this.loadSeq;
    this.idZone ??= '';
    if (this.startDate === undefined || this.endDate === undefined) {
      this.startDate = moment().startOf('isoWeek').toISOString();
      this.endDate = moment(this.startDate).add(6, 'days').toISOString();
    }

    // all_groups: los solicitantes piloto viven en otros grupos (ej. Garbri),
    // así que el panel admin pide los pedidos/cambios de TODAS las estaciones;
    // el back solo lo honra para los correos de MOBICO_PIPES_ADMIN_EMAILS.
    // Periodo (semana seleccionada / mes actual / mes anterior): los cambios se
    // consultan por semana, así que con un mes va una llamada por semana ISO y
    // se fusionan los buckets por tipo.
    const range = this.requestRange();
    const weeks = this.requestPeriod === 'week' ? this.selectedWeeks() : this.weeksIn(range.start, range.end);
    forkJoin([
      this.gasprecioService.getListPurchases(this.idGroup, range.start, range.end, this.idZone, 'orders', this.stationFilter, '', true),
      // Sin semanas (selector múltiple en «Ninguna»): forkJoin([]) nunca emite.
      weeks.length
        ? forkJoin(weeks.map((week) => this.fuelOrders.getChanges({ group: this.idGroup, zones: this.idZone, week, station: this.stationFilter, all_groups: 'true' })))
        : of([] as any[])
    ]).subscribe({
      next: ([orders, changesResults]: any) => {
        if (seq !== this.loadSeq) { return; }
        const rows = this.rowsInSelectedWeeks(orders || []).map((o: any) => ({ ...o, material: o.material || this.productLabel(o.product) }));

        const changes: any = {};
        (changesResults || []).forEach((data: any) => {
          Object.entries(data?.changes || {}).forEach(([type, list]: [string, any]) => {
            changes[type] = [...(changes[type] || []), ...(list || [])];
          });
        });
        // Valor nuevo solicitado, ya formateado para la columna "Nuevo valor".
        const newValueOf = (type: string, c: any): string => {
          if (type === 'date' && c.new_delivery_date) { return moment.utc(c.new_delivery_date).format('DD/MM/YY'); }
          if (type === 'capacity' && c.new_truck_capacity) { return new Intl.NumberFormat('es-MX').format(c.new_truck_capacity); }
          if (type === 'product') {
            // Lo nuevo de un cambio de producto es el PRODUCTO (Supreme); el material va en el tooltip
            // (`new_value_title`). Solo si el producto es el mismo (cambio de material) se muestra el material.
            const opt = this.productOptions.find((p) => p.value === c.new_product);
            const label = opt ? opt.label : c.new_product;
            return (c.new_product && c.new_product !== c.product ? label : c.new_material || label) || '—';
          }
          return '—';
        };
        // Tooltip del valor nuevo de un cambio de producto: producto y material pedidos.
        const newValueTitleOf = (type: string, c: any): string => {
          if (type !== 'product') { return ''; }
          const opt = this.productOptions.find((p) => p.value === c.new_product);
          return [opt ? opt.label : c.new_product, c.new_material].filter(Boolean).join(' · ');
        };
        const normalize = (type: string, c: any) => ({
          ...c,
          request_date: c.created_at, // cuándo se creó la solicitud de cambio
          change_kind: type,
          type_label: this.changeTypeLabels[type],
          station_name: c.station,
          order_delivery_date: c.delivery_date,
          material: c.material || this.productLabel(c.product),
          new_value: newValueOf(type, c),
          new_value_title: newValueTitleOf(type, c),
          // El PO lleva el código del producto: un cambio de producto trae el PO del producto nuevo
          // (`new_po_number`) y la fila lo muestra aunque MOBICO no haya aprobado; el anterior queda en el tooltip.
          po_number: type === 'product' && c.new_po_number ? c.new_po_number : c.po_number,
          previous_po_number: c.po_number || null,
          statusName: this.changeStatusDisplay(type, c),
        });

        // Cambio EN VUELO por pedido (a lo más uno: el backend rechaza un segundo);
        // define lo que muestra la fila del pedido (Aprobar/Rechazar responden ese cambio).
        const inFlightByOrder = new Map<string, any>();
        // Historial: TODOS los cambios (cualquier estatus) aplanados por pedido, para el
        // desglose expandible. Cada evento hereda luego el contexto del pedido para
        // renderizarse con todas las columnas de la tabla.
        const allChanges: any[] = [];
        ['addition', 'cancel', 'date', 'capacity', 'product'].forEach((type) => {
          (changes[type] || []).forEach((c: any) => {
            if (!c.order_id) { return; }
            if (this.isChangeInFlight(c)) {
              // _ts: el cambio en vuelo también cuenta para «lo pedido originalmente» (originalRequestValues ordena por él).
              inFlightByOrder.set(String(c.order_id), { ...normalize(type, c), in_flight_change: true, _ts: new Date(c.created_at).getTime() });
            }
            allChanges.push({
              is_history: true,
              // Identidad estable de la fila (doc de cambio): la usa rowIdentity para
              // que ngx-datatable reutilice el DOM y no re-anime filas ya visibles.
              _id: c._id,
              order_id: String(c.order_id),
              change_kind: type,
              type_label: this.changeTypeLabels[type],
              request_date: c.created_at,
              new_value: newValueOf(type, c),
              // La fila del historial muestra la solicitud tal como estaba ANTES de este cambio (previous_* del item: PO,
              // producto, material, fecha y capacidad); «Nuevo valor» lleva lo pedido y el pedido (fila de arriba), lo vigente.
              po_number: c.previous_po_number || c.po_number,
              product: c.product,
              material: c.material || this.productLabel(c.product),
              truck_capacity: c.truck_capacity,
              order_delivery_date: c.delivery_date,
              // PO al que pasó el pedido con este cambio (si lo aprobaron): el anterior ya no se reutiliza.
              po_new: type === 'product' && c.status === 'confirmed' && c.new_po_number && c.new_po_number !== (c.previous_po_number || c.po_number)
                ? c.new_po_number
                : null,
              history_reason: c.reason ? `Motivo de la estación: ${c.reason}` : '',
              status: c.status,
              statusName: this.changeStatusDisplay(type, c),
              week_status: c.week_status,
              // updated_at = cuándo respondió MOBICO (resueltos): alimenta el
              // orden por última actividad de la tabla y decide si un rechazo es lo último que pasó al pedido.
              updated_at: c.updated_at,
              _ts: new Date(c.created_at).getTime(),
              _event_ts: new Date(c.updated_at || c.created_at).getTime() || 0,
            });
          });
        });

        this.orderRows = rows;
        this.inFlightByOrder = inFlightByOrder;
        this.changesRows = allChanges;
        this.spaceByKey = new Map();
        this.rebuildRequests();
        this.loadCapacityRisks();

        this.maybeShowNewRequestNotices();
      },
      complete: () => {
        if (seq !== this.loadSeq) { return; }
        this.reqst = 1;
        this.finishLoad();
        // reqst=1 recién renderiza la tabla (*ngIf): un solo recálculo de
        // anchos con el layout ya estable (ver scheduleTableRecalc).
        this.scheduleTableRecalc();
      },
      error: (err) => {
        if (seq !== this.loadSeq) { return; }
        console.error(err);
        this.reqst = 1;
        this.finishLoad();
        this.scheduleTableRecalc();
      }
    });
  }

  /**
   * Aviso único al admin de las solicitudes NUEVAS por revisar (pedidos, cambios y
   * cancelaciones): un toast breve con el conteo (el detalle vive en la tabla). Los
   * ids ya avisados se guardan en localStorage; la primera vez solo se registra lo
   * existente, sin aviso.
   */
  private maybeShowNewRequestNotices(): void {
    // Solo lo aún por responder genera aviso (la tabla también muestra lo resuelto).
    // Sobre TODOS los pedidos, no solo la página visible (this.requests pagina).
    const pending = this.allParents.filter((r: any) => this.isActionable(r));
    const allIds = pending.map((x: any) => String(x._id));

    let seen: any = null;
    try { seen = JSON.parse(localStorage.getItem(this.noticeStorageKey)); } catch { seen = null; }
    if (!Array.isArray(seen)) {
      localStorage.setItem(this.noticeStorageKey, JSON.stringify(allIds));
      return;
    }

    const seenSet = new Set(seen.map(String));
    const news = pending.filter((x: any) => !seenSet.has(String(x._id)));
    if (!news.length) { return; }
    // Si hay un Swal abierto (otro toast o un diálogo del legacy), reintenta en el
    // siguiente refresh: Swal es de instancia única y dispararía el cierre del actual.
    if (Swal.isVisible()) { return; }

    // Se marcan como avisados ANTES de mostrar: el aviso es una sola vez.
    localStorage.setItem(this.noticeStorageKey, JSON.stringify([...seen.map(String), ...news.map((x: any) => String(x._id))]));

    // Desglose por tipo en CHIPS (task 2026-07-10 "organizar alertas": el renglón
    // corrido "Producto (2) · Capacidad (2) · ..." se partía en líneas ilegibles
    // con muchos tipos). Cada tipo sale como chip soft-tint con contador — los
    // MISMOS colores que la columna Tipo de la tabla (.type-chip), así el aviso
    // se escanea igual que el triage. Orden: por volumen descendente (lo más
    // cargado primero). Con una sola solicitud, su chip + PO y estación.
    // Estilos en styles.scss bajo .pipes-toast (el toast vive en <body>).
    let detail: string;
    if (news.length === 1) {
      const r = news[0];
      detail =
        `<div class="pipes-toast__chips">${this.typeChipHtml(r.change_kind, r.type_label)}${this.productChipHtml(r.product)}</div>` +
        `<div class="pipes-toast__note">PO ${this.esc(r.po_number || '—')} · ${this.esc(r.station_name || '')}</div>`;
    } else {
      // Por tipo Y producto (Edey 20-oct: badge de producto al lado del tipo): «Adición · Extra ×3».
      const chips = this.pipesExport.typeProductChipsHtml(news.map((r: any) => ({
        kind: r.change_kind, label: r.type_label, product: r.product || '', productLabel: this.productLabel(r.product),
      })));
      detail =
        `<div class="pipes-toast__chips">${chips}</div>` +
        `<div class="pipes-toast__note">El detalle está en la tabla.</div>`;
    }
    const title = news.length === 1
      ? 'Solicitud nueva por revisar'
      : `${news.length} solicitudes nuevas por revisar`;
    this.showToast('info', title, detail);
  }

  /** Chip de tipo y badge de producto del html de los toasts (PipesExportService, mismos que el solicitante). */
  private typeChipHtml(kind: string, label: string, count?: number): string {
    return this.pipesExport.typeChipHtml(kind, label, count);
  }
  private productChipHtml(product: string, count?: number): string {
    return this.pipesExport.productChipHtml(product, this.productLabel(product), count);
  }

  /** Toast del flujo: aviso breve no bloqueante que se cierra solo. Con texto de
   *  detalle se fija un ancho cómodo para que la línea no se parta en pedazos.
   *  Look moderno del flujo vía customClass .pipes-toast (styles.scss, espejo
   *  del showToast del solicitante): card blanca radio 12 + ícono FA en círculo
   *  soft-tint en lugar del aro animado default de Swal. */
  /** Toast del flujo (PipesExportService): mismo look en admin y solicitante. */
  private showToast(icon: 'success' | 'error' | 'info', title: string, html?: string): void {
    this.pipesExport.showToast(icon, title, html);
  }

  private esc(v: unknown): string {
    return this.pipesExport.esc(v);
  }

  /** Fecha de entrega YA solicitada (YYYY-MM-DD, calendario UTC como las pinta la app). En un cambio de fecha en vuelo
   *  es la NUEVA: `order_delivery_date` trae la anterior porque el cambio no toca el pedido hasta que se aprueba. */
  private requestedDeliveryDay(row: any): string {
    return pipesRows.requestedDeliveryDay(row);
  }

  /** Producto YA solicitado: el nuevo si el cambio en vuelo es de producto. */
  private requestedProduct(row: any): string {
    return pipesRows.requestedProduct(row);
  }

  /**
   * Lo que MOBICO acepta distinto a lo solicitado (producto, fecha de entrega, litros), con el valor de antes y el de
   * ahora, en vivo mientras el admin captura el modal. Si hay alguno se pide el motivo. Costo por litro y total no
   * cuentan: la estación nunca los pide.
   */
  get confirmAdjustments(): AdjustmentLine[] {
    const requested = this.confirmRequested;
    const action = this.confirmAction;
    if (!requested || !action || !action.needsDeliveryDate || action.status !== 'confirmed') { return []; }
    const out: AdjustmentLine[] = [];
    const dmy = (day: string) => moment(day, 'YYYY-MM-DD').format('DD/MM/YY');
    const fmt = (n: number) => new Intl.NumberFormat('es-MX').format(n);
    if (this.confirmProduct && requested.product && this.confirmProduct !== requested.product) {
      out.push({ field: 'product', label: 'Producto', from: this.productLabel(requested.product), to: this.productLabel(this.confirmProduct) });
    }
    if (this.confirmDeliveryDate && requested.date && this.confirmDeliveryDate !== requested.date) {
      out.push({ field: 'delivery_date', label: 'Fecha de entrega', from: dmy(requested.date), to: dmy(this.confirmDeliveryDate) });
    }
    const liters = this.litersForCalc;
    if (liters != null && requested.liters != null && liters !== requested.liters) {
      out.push({ field: 'liters', label: 'Litros', from: fmt(requested.liters), to: fmt(liters) });
    }
    return out;
  }

  /** Tooltip de la marca «aprobada con ajustes» del pedido: cada ajuste con su valor anterior y el motivo (más reciente primero). */
  adjustmentsTitle(row: any): string {
    return this.pipesAdjustments.adjustmentsTitle(row?.adjustments, this.productLabeler);
  }

  /** Tooltip de la celda «PO» de una fila del historial: el PO al que pasó el pedido (este ya no se reutiliza). */
  historyPoTitle(row: any): string {
    return this.pipesAdjustments.historyPoTitle(row);
  }

  /**
   * Aprueba un pedido o una solicitud de cambio (status: confirmed). La respuesta al
   * solicitante es el propio estatus (sin texto libre). Si es un cambio, el backend
   * lo aplica al pedido original y CONFIRMA el pedido completo (aunque siguiera
   * pendiente): aprobar el cambio es aprobar la solicitud.
   */
  approveOrder(row: any): void {
    // Respaldo del [disabled] del botón: lo ya respondido no se re-responde.
    if (!this.isActionable(row)) { return; }
    this.confirmError = '';
    // Fecha de entrega: se captura al aprobar pedidos primarios Y cambios (aprobar
    // el cambio confirma el pedido completo, así que también se fecha). Solo la
    // cancelación no la pide: aprobarla cancela el pedido, no hay entrega.
    // Prefill en UTC: las fechas del back son medianoche UTC y moment() local las
    // corría un día en MX. En un cambio de fecha en vuelo la solicitada es la NUEVA (order_delivery_date trae la
    // anterior: el cambio no se aplica al pedido hasta aprobarlo), ver requestedDeliveryDay.
    const needsDeliveryDate = !(row.in_flight_change && row.change_kind === 'cancel');
    const requestedDay = needsDeliveryDate ? this.requestedDeliveryDay(row) : '';
    this.confirmDeliveryDate = requestedDay;
    // La entrega no puede ser anterior a HOY: si la fecha solicitada ya pasó
    // (aprobación tardía), el prefill se ajusta al día corriente para no abrir
    // el modal con el botón Aprobar bloqueado sin motivo visible.
    if (this.confirmDeliveryDate && this.confirmDeliveryDate < this.minDeliveryDate) {
      this.confirmDeliveryDate = this.minDeliveryDate;
    }
    // Litros aceptados: se precargan con los efectivos tras el cambio a aprobar (el
    // admin los ajusta a lo que Mobico realmente acepta). Solo aplica cuando hay
    // entrega (no en cancelaciones).
    this.confirmLiters = needsDeliveryDate ? this.defaultAcceptedLiters(row) : null;
    // Producto, fecha y litros YA solicitados: base para detectar lo que MOBICO acepta distinto (ver confirmAdjustments).
    this.confirmRequested = needsDeliveryDate
      ? { product: this.requestedProduct(row), date: requestedDay, liters: this.confirmLiters }
      : null;
    this.confirmProduct = needsDeliveryDate ? this.requestedProduct(row) : '';
    this.confirmAction = { row, status: 'confirmed', title: 'Aprobar solicitud', actionLabel: 'Aprobar', successTitle: 'Solicitud aprobada', needsDeliveryDate };
    this.refreshConfirmSpace();
  }

  /** Candado de inventario del Aprobar: espacio disponible a la fecha de entrega
   *  capturada, con el producto EFECTIVO tras aprobar (el nuevo si el cambio en
   *  vuelo es de producto) y excluyendo al pedido que se aprueba (sus litros se
   *  reemplazan con los aceptados). Mejor-esfuerzo: sin dato no se bloquea. */
  refreshConfirmSpace(): void {
    this.confirmSpace = null;
    this.confirmSpaceChecked = false;
    this.confirmSpaceBasis = null;
    this.confirmTankCapacity = null;
    this.confirmInventoryUsed = null;
    this.confirmInventoryDate = null;
    this.confirmCarryOver = null;
    const action = this.confirmAction;
    const row = action?.row;
    if (!action || !action.needsDeliveryDate || !row) { return; }
    const product = this.confirmProduct || this.requestedProduct(row);
    // station_id: presente en pedidos (purchases) y, desde el fix del back
    // 2026-07-15, también en las filas de cambios.
    const stationId = row.station_id || '';
    const excludeId = row.in_flight_change ? (row.order_id || '') : row._id;
    if (!stationId || !product || !this.confirmDeliveryDate) {
      // Sin estación/producto/fecha no hay qué consultar: el pill gris lo
      // anuncia (no pintar nada es indistinguible de un error).
      this.confirmSpaceChecked = true;
      return;
    }
    this.confirmSpaceLoading = true;
    this.fuelOrders
      .getSuggest(String(stationId), product, this.confirmDeliveryDate, String(excludeId || ''))
      .subscribe({
        next: (data: any) => {
          this.confirmSpaceLoading = false;
          this.confirmSpaceChecked = true;
          this.confirmSpace = data?.available_space ?? null;
          this.confirmSpaceBasis = data?.space_basis ?? null;
          this.confirmTankCapacity = data?.tank_capacity ?? null;
          this.confirmInventoryUsed = data?.inventory_used ?? null;
          this.confirmInventoryDate = data?.inventory_date ?? null;
          this.confirmCarryOver = data?.carry_over ?? null;
        },
        error: () => {
          this.confirmSpaceLoading = false;
          this.confirmSpaceChecked = true;
        },
      });
  }

  /** true si los litros aceptados exceden el espacio disponible conocido. */
  get confirmOverSpace(): boolean {
    return (
      this.confirmSpace != null &&
      this.confirmLiters != null &&
      this.confirmLiters > this.confirmSpace
    );
  }

  /** Desglose "¿Por qué no hay espacio?" del Aprobar (réplica del spaceInfo del
   *  solicitante): lo abre el pill rojo. */
  openConfirmSpaceInfo(): void {
    this.spaceInfo = {
      tank: this.confirmTankCapacity,
      inventory: this.confirmInventoryUsed,
      basis: this.confirmSpaceBasis,
      invDate: this.confirmInventoryDate,
      carry: this.confirmCarryOver,
      available: this.confirmSpace != null ? Math.max(0, this.confirmSpace) : null,
      requested: this.confirmLiters,
    };
  }

  closeSpaceInfo(): void {
    this.spaceInfo = null;
  }

  /** Rechaza un pedido o una solicitud de cambio (status: rejected). */
  rejectOrder(row: any): void {
    // Respaldo del [disabled] del botón: lo ya respondido no se re-responde.
    if (!this.isActionable(row)) { return; }
    this.confirmError = '';
    this.confirmDeliveryDate = '';
    this.resetDeliveryCapture();
    this.confirmAction = { row, status: 'rejected', title: 'Rechazar solicitud', actionLabel: 'Rechazar', successTitle: 'Solicitud rechazada', needsDeliveryDate: false };
  }

  /** Cierra el modal de confirmación sin responder. */
  cancelConfirmAction(): void {
    if (this.respondingOrder) { return; }
    this.confirmAction = null;
    this.confirmError = '';
    this.confirmDeliveryDate = '';
    this.showDeliveryCalendar = false;
    this.resetDeliveryCapture();
    // En modalOnly cerrar sin responder devuelve el control al host (que
    // destruye esta instancia).
    if (this.modalOnly) { this.modalClosed.emit(false); }
  }

  /** Limpia los campos de entrega capturados al aprobar. */
  private resetDeliveryCapture(): void {
    this.confirmLiters = null;
    this.confirmProduct = '';
    this.confirmRequested = null;
  }

  // ---- Mini calendario de "Fecha de entrega" (task 2026-07-13 "igual que el
  // selector del cambio de fecha"; réplica scoped del op-mini-cal de
  // pipes.component — patrón de duplicación tipo type-chip). La fecha SOLICITADA
  // por la estación se marca en gris como referencia (SÍ seleccionable: entregar
  // el día pedido es válido, a diferencia del cambio de fecha donde elegir la
  // actual no cambiaría nada) y la fecha de entrega elegida va en verde. ----
  showDeliveryCalendar = false;
  // Posición del panel flotante (capa fija): anclado al campo por su borde
  // IZQUIERDO — el campo es el primero del renglón del modal, al revés del
  // modal de cambio que ancla por la derecha.
  calPanelPos: { top: string; left: string } | null = null;

  toggleDeliveryCalendar(ev: MouseEvent): void {
    this.showDeliveryCalendar = !this.showDeliveryCalendar;
    if (!this.showDeliveryCalendar) { return; }
    const r = (ev.target as HTMLElement).getBoundingClientRect();
    // Altura real del picker ≈ 320px (6 filas + weekdays + header + leyenda).
    const panelH = 320;
    const panelW = 300;
    const gap = 6;
    let top = r.bottom + gap; // preferencia: SIEMPRE abajo del campo
    if (top + panelH > window.innerHeight - 12) {
      top = Math.max(12, r.top - gap - panelH);
    }
    const left = Math.min(Math.max(16, r.left), window.innerWidth - panelW - 16);
    this.calPanelPos = { top: `${top}px`, left: `${left}px` };
  }

  onCalSelect(d: { year: number; month: number; day: number }): void {
    this.confirmDeliveryDate = this.calKey(d);
    this.showDeliveryCalendar = false;
    // El espacio está proyectado a la fecha de entrega: cambiarla lo recalcula.
    this.refreshConfirmSpace();
  }

  /** Mes inicial: el de la fecha ya elegida (prefill = la solicitada). */
  get calStartDate(): { year: number; month: number } | null {
    if (!this.confirmDeliveryDate) { return null; }
    const m = moment(this.confirmDeliveryDate, 'YYYY-MM-DD');
    return { year: m.year(), month: m.month() + 1 };
  }

  /** Hoy (MX) como tope inferior: no se aprueba una entrega en el pasado. */
  get calMinDate(): { year: number; month: number; day: number } {
    const t = moment.tz('America/Mexico_City');
    return { year: t.year(), month: t.month() + 1, day: t.date() };
  }

  private calKey(d: { year: number; month: number; day: number }): string {
    return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
  }

  /** Día de la fecha REQUERIDA por la estación (gris de referencia). */
  isCalRequested(d: { year: number; month: number; day: number }): boolean {
    const cur = this.confirmRequested?.date;
    return !!cur && cur === this.calKey(d);
  }

  /** Día elegido como fecha de entrega (relleno verde). */
  isCalSelected(d: { year: number; month: number; day: number }): boolean {
    return !!this.confirmDeliveryDate && this.confirmDeliveryDate === this.calKey(d);
  }

  /** Litros válidos para multiplicar/dividir: finito y > 0 (evita /0 y NaN). */
  private get litersForCalc(): number | null {
    const l = this.confirmLiters;
    return l != null && Number.isFinite(l) && l > 0 ? l : null;
  }

  /**
   * Litros a precargar en el modal de aprobación: el valor efectivo tras aplicar el
   * cambio que se aprueba. En un cambio de capacidad en vuelo el nuevo valor viene en
   * new_truck_capacity (el resto de campos de la fila son el "antes"); en los demás
   * casos (otros cambios o pedido primario) liters_requested ya es el vigente.
   */
  private defaultAcceptedLiters(row: any): number | null {
    if (row.in_flight_change && row.change_kind === 'capacity') {
      return row.new_truck_capacity ?? row.liters_requested ?? null;
    }
    // Cambio de OTRA cosa (fecha, producto) sobre una pipa ya aceptada: los litros vigentes son los que MOBICO aceptó
    // (`truck_capacity` del cambio guarda lo vigente al pedirlo), no los solicitados: si no, aprobar volvería a subirlos.
    if (row.in_flight_change) {
      return row.truck_capacity ?? row.liters_requested ?? null;
    }
    return row.liters_requested ?? null;
  }

  // ---- Aviso de capacidad (junta MOBICO + Garbri 27-oct: MOBICO cambia la capacidad o manda pipas más grandes y no
  // caben en el tanque). Espacio del tanque por estación+producto+día (POST /fuel-orders/space-check) y capacidades por
  // estación; la marca de cada fila sale de pipesRows.capacityRisk. Se recarga con cada carga de la tabla.
  private spaceByKey = new Map<string, number | null>();
  private capacitiesByStation = new Map<string, number[]>();
  private spaceCheckSeq = 0;

  private spaceKey(station: string, product: string, day: string): string {
    return `${station}|${product}|${day}`;
  }

  /** Pide el espacio de las pipas vivas cargadas (una vez por estación+producto+día; lo ya pedido no se repite). */
  private loadCapacityRisks(): void {
    const items = new Map<string, { station: string; product: string; date: string }>();
    (this.allParents || []).forEach((row: any) => {
      const t = pipesRows.capacityCheckTarget(row);
      if (!t || !row.station_id) { return; }
      const station = String(row.station_id);
      const key = this.spaceKey(station, t.product, t.day);
      if (!this.spaceByKey.has(key)) { items.set(key, { station, product: t.product, date: t.day }); }
    });
    const seq = ++this.spaceCheckSeq;
    if (!items.size) { return; }
    this.fuelOrders.getSpaceCheck([...items.values()]).subscribe({
      next: (data: any[]) => {
        if (seq !== this.spaceCheckSeq) { return; }
        data.forEach((d: any) => {
          this.spaceByKey.set(this.spaceKey(String(d.station), d.product, d.date), d.available_space ?? null);
          if (Array.isArray(d.capacities)) { this.capacitiesByStation.set(String(d.station), d.capacities.map(Number)); }
        });
        this.rebuildRequests();
      },
      // Mejor esfuerzo: sin dato no hay marca (el candado del Aprobar sigue igual).
      error: () => undefined,
    });
  }

  /** Riesgo de capacidad de la fila (null = sin marca). */
  capacityRisk(row: any): pipesRows.CapacityRisk | null {
    const t = pipesRows.capacityCheckTarget(row);
    if (!t || !row?.station_id) { return null; }
    const key = this.spaceKey(String(row.station_id), t.product, t.day);
    if (!this.spaceByKey.has(key)) { return null; }
    return pipesRows.capacityRisk(row, this.spaceByKey.get(key), this.capacitiesByStation.get(String(row.station_id)));
  }

  /** Tooltip de Capacidad: el de siempre y, si hay riesgo, el espacio del tanque y qué pipa ya no cabe. */
  capacityTooltip(row: any): string {
    const base = this.capacityTitle(row);
    const risk = row?._capRisk as pipesRows.CapacityRisk | null;
    return risk ? `${base}\n\n${pipesRows.capacityRiskText(risk)}` : base;
  }

  /** Siguiente capacidad de la estación que ya no cabe en el espacio del Aprobar (pill ámbar del modal); null si cabe. */
  get confirmNextCapacityOver(): number | null {
    const row = this.confirmAction?.row;
    if (!row || this.confirmSpace == null || this.confirmLiters == null || this.confirmOverSpace) { return null; }
    const next = pipesRows.nextCapacity(this.confirmLiters, this.capacitiesByStation.get(String(row.station_id)));
    return next != null && next > this.confirmSpace ? next : null;
  }

  /**
   * Valor a mostrar en la columna Capacidad: si Mobico ya definió los litros
   * aceptados/entregados (liters), se muestra ese valor; si no, la capacidad
   * solicitada (truck_capacity).
   */
  capacityValue(row: any): number | null {
    return row?.liters != null ? row.liters : (row?.truck_capacity ?? null);
  }

  /**
   * Tooltip de la columna Capacidad. Con litros aceptados detalla lo solicitado, lo
   * aceptado por Mobico y la diferencia entre ambos; si aún no hay aceptado, indica
   * que es la capacidad solicitada.
   */
  capacityTitle(row: any): string {
    if (row?.liters == null) { return 'Capacidad solicitada'; }
    const fmt = (n: number) => new Intl.NumberFormat('es-MX').format(n);
    const requested = row.liters_requested ?? row.truck_capacity ?? null;
    const accepted = row.liters;
    if (requested == null) { return `Aceptado por Mobico: ${fmt(accepted)} L`; }
    const diff = accepted - requested;
    const diffText = diff === 0 ? 'sin diferencia' : `${diff > 0 ? '+' : '-'}${fmt(Math.abs(diff))} L`;
    // Saltos de línea: el tooltip (.cap-tooltip) los respeta vía white-space: pre-line
    return `Solicitado: ${fmt(requested)} L\nAceptado: ${fmt(accepted)} L\nDiferencia: ${diffText}`;
  }

  /**
   * Muestra el icono de info en Capacidad solo cuando el pedido está confirmado y
   * los litros aceptados difieren de lo solicitado (Mobico entregó una cantidad
   * distinta a la pedida).
   */
  showCapacityDiff(row: any): boolean {
    const requested = row?.liters_requested ?? row?.truck_capacity ?? null;
    return row?.status === 'confirmed' && row?.liters != null && requested != null && row.liters !== requested;
  }

  /**
   * Piso de la fecha de entrega al aprobar: el día corriente (feedback 2026-07-10
   * "no se debe poder aprobar con una fecha anterior"). Getter para que siga
   * siendo válido si el modal queda abierto cruzando la medianoche.
   */
  get minDeliveryDate(): string {
    return moment().format('YYYY-MM-DD');
  }

  /** true si la fecha capturada existe pero es anterior a hoy (bloquea Aprobar). */
  get deliveryDateTooEarly(): boolean {
    // En «Realizar cambio» (pedido ya aprobado) la fecha vigente se puede dejar como está aunque ya haya pasado (pipa
    // retrasada): solo se bloquea una fecha NUEVA anterior a hoy.
    if (this.confirmAction?.edit && this.confirmDeliveryDate === this.confirmRequested?.date) { return false; }
    // Comparación lexicográfica: válida en formato YYYY-MM-DD.
    return !!this.confirmDeliveryDate && this.confirmDeliveryDate < this.minDeliveryDate;
  }

  /** Al aprobar, los datos de entrega (fecha, litros, costos) son OPCIONALES
   *  (feedback 2026-07-13: el admin no está obligado a capturarlos; revierte
   *  la obligatoriedad del fix 2026-07-10). Bloquean Aprobar: una fecha de
   *  entrega anterior a hoy, o litros aceptados por encima del espacio
   *  disponible (candado de inventario 2026-07-15). */
  get confirmActionValid(): boolean {
    const action = this.confirmAction;
    if (!action) { return false; }
    if (!action.needsDeliveryDate) { return true; }
    return !this.deliveryDateTooEarly && !this.confirmOverSpace;
  }

  /**
   * Envía la respuesta (aprobar/rechazar). El botón muestra el avance (evita dobles
   * clicks); si el API falla, el error queda inline en el propio modal.
   */
  async confirmRespond(): Promise<void> {
    const action = this.confirmAction;
    if (!action || this.respondingOrder || !this.confirmActionValid) { return; }
    this.respondingOrder = true;
    this.confirmError = '';
    try {
      const body: { status?: string; delivery_date?: string; liters?: number; product?: string; as_change?: boolean } = { status: action.status };
      // Cambio directo sobre un pedido ya aprobado: PATCH sin status + as_change (el API lo trata como cambio del admin).
      if (action.edit) {
        delete body.status;
        body.as_change = true;
      }
      // Ajustes de MOBICO (se leen ANTES de limpiar el modal): lo que se aceptó distinto a lo solicitado, para el aviso.
      const adjustments = this.confirmAdjustments;
      if (action.needsDeliveryDate) {
        // Todos los datos de entrega son opcionales: solo viajan los capturados.
        if (this.confirmDeliveryDate) { body.delivery_date = this.confirmDeliveryDate; }
        if (this.confirmLiters != null) { body.liters = this.confirmLiters; }
        // Producto solo si es otro que el solicitado (el API cambia el PO con él).
        if (this.confirmProduct && this.confirmRequested && this.confirmProduct !== this.confirmRequested.product) {
          body.product = this.confirmProduct;
        }
      }
      // Detalle del toast de éxito (mismo lenguaje que el aviso de solicitudes
      // nuevas: chip de tipo + PO/estación); se arma ANTES de limpiar el estado
      // del modal porque la fecha/litros capturados viven ahí. Al aprobar con
      // entrega se anexa lo pactado para que el aviso confirme lo que se guardó.
      const row = action.row;
      let toastDetail =
        `<div class="pipes-toast__chips">${this.typeChipHtml(row.change_kind, row.type_label)}${this.productChipHtml(row.product)}</div>` +
        `<div class="pipes-toast__note">PO ${this.esc(row.po_number || '—')} · ${this.esc(row.station_name || '')}</div>`;
      if (action.status === 'confirmed' && action.needsDeliveryDate && this.confirmDeliveryDate) {
        // Lo pactado que NO cambió respecto a lo solicitado; lo ajustado va en el desglose de abajo.
        const adjusted = new Set(adjustments.map((a) => a.field));
        const parts: string[] = [];
        if (!adjusted.has('delivery_date')) { parts.push(`Entrega ${moment(this.confirmDeliveryDate).format('DD/MM/YYYY')}`); }
        if (this.confirmLiters != null && !adjusted.has('liters')) {
          parts.push(`${new Intl.NumberFormat('es-MX').format(this.confirmLiters)} lts.`);
        }
        if (parts.length) { toastDetail += `<div class="pipes-toast__note">${parts.join(' · ')}</div>`; }
      }
      if (action.status === 'confirmed' && adjustments.length) {
        // Ajustes de MOBICO en grid «campo | antes → después» (Edey 19-oct: la frase corrida era mucha info).
        toastDetail +=
          `<div class="pipes-toast__changes">` +
          adjustments
            .map((a) =>
              `<span class="pipes-toast__change-label">${this.esc(a.label)}</span>` +
              `<span class="pipes-toast__change-value"><span class="pipes-toast__change-old">${this.esc(a.from)}</span> <i class="fa fa-long-arrow-right" aria-hidden="true"></i> <b>${this.esc(a.to)}</b></span>`)
            .join('') +
          `</div><div class="pipes-toast__note">Se le avisa a la estación.</div>`;
      }
      await firstValueFrom(this.fuelOrders.updateOrderStatus(action.row._id, body));
      this.confirmAction = null;
      this.confirmDeliveryDate = '';
      this.showDeliveryCalendar = false;
      this.resetDeliveryCapture();
      // En modalOnly no hay tabla que recargar: el host refresca sus chips al
      // recibir el cierre con respuesta guardada.
      if (this.modalOnly) {
        this.modalClosed.emit(true);
      } else {
        this.getData();
        // Cronograma y Sugerencias de la misma página (app-pipes cronoOnly) se recargan con la respuesta guardada.
        this.fuelOrders.notifyOrdersChanged();
      }
      this.showToast('success', action.successTitle, toastDetail);
    } catch (err: any) {
      console.error('Error respondiendo solicitud', err);
      this.confirmError = err?.error?.err || 'No se pudo guardar, intenta de nuevo.';
    } finally {
      this.respondingOrder = false;
    }
  }

}
